import type { ReactNode } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { DnsRecordWorkspaceConfigurationReadModel } from '@r/api/generated/client.schemas.ts';
import { Button } from '@ui/button.tsx';
import { Card, CardContent } from '@ui/card.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { cn } from '@r/utils/utils.ts';
import { ConfigurationStatusBadge } from './configuration-status-badge.tsx';
import { DnsRecordsTable } from './dns-records-table.tsx';
import {
  type ConfigurationScope,
  useWorkspaceConfigurationValidation
} from './use-workspace-configuration-validation.ts';

interface ConfigurationPageLayoutProps {
  scope: ConfigurationScope;
  title: string;
  description: string;
  workspaceId: number | undefined;
  // Ce qui est authentifié : le domaine, ou l'adresse d'envoi
  subject: ReactNode;
  isValid: boolean | undefined;
  records: DnsRecordWorkspaceConfigurationReadModel[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/*
 * Gabarit des pages d'authentification du nom de domaine et des adresses e-mails : état des enregistrements
 * DNS et vérification à la demande.
 */
export const ConfigurationPageLayout = ({
  scope,
  title,
  description,
  workspaceId,
  subject,
  isValid,
  records,
  isLoading,
  isError
}: ConfigurationPageLayoutProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { validate, isValidating } = useWorkspaceConfigurationValidation(workspaceId, scope);

  return (
    <div className="flex flex-col space-y-10 grow">
      <h1 className="text-4xl font-bold">{title}</h1>

      <Card className="w-full">
        <CardContent className="p-8 space-y-6">
          <div className="space-y-2">
            <h2 className="font-semibold">{t('workspace.configuration.records.title')}</h2>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {subject ? (
              <span className="text-sm font-medium">{subject}</span>
            ) : isLoading ? (
              <Skeleton className="w-40 h-4" />
            ) : (
              <span className="text-sm font-medium">—</span>
            )}
            <ConfigurationStatusBadge isValid={isValid} isLoading={isLoading || isValidating} />
            <Button
              size="custom_sm"
              onClick={() => validate()}
              disabled={isValidating || !workspaceId}
              className="ml-auto"
            >
              <RefreshCw className={cn('w-4 h-4', isValidating && 'animate-spin')} />
              {t('workspace.configuration.validate.button')}
            </Button>
          </div>

          <div className="space-y-2">
            {isLoading && (
              <div className="space-y-2">
                <Skeleton className="w-full h-10" />
                <Skeleton className="w-full h-10" />
              </div>
            )}
            {isError && (
              <p className="text-sm text-destructive">{t('workspace.configuration.error')}</p>
            )}
            {!isLoading && !isError && records && <DnsRecordsTable records={records} />}
            <p className="text-xs text-muted-foreground">
              {t('workspace.configuration.propagation')}
            </p>
          </div>
        </CardContent>
      </Card>

      <div>
        <Button onClick={() => navigate({ to: '/settings/my-workspace' })}>
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>
      </div>
    </div>
  );
};
